import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { ShoppingBag, User, Menu, X, LogOut, Shield, Search } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Search Component
const SearchBar = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
        setMobileSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Real-time search
  useEffect(() => {
    const searchProducts = async () => {
      if (query.trim().length < 2) {
        setResults([]);
        setIsOpen(false);
        return;
      }

      setLoading(true);
      try {
        const response = await axios.get(`${API}/products`);
        const searchTerms = query.toLowerCase().split(' ').filter(t => t.length > 0);
        
        const filtered = response.data.filter(product => {
          const name = product.name?.toLowerCase() || '';
          const slug = product.slug?.toLowerCase() || '';
          const category = product.category?.toLowerCase() || '';
          const description = product.description?.toLowerCase() || '';
          const colors = (product.colors || []).join(' ').toLowerCase();
          
          // Combined searchable text
          const searchableText = `${name} ${slug} ${category} ${description} ${colors}`;
          
          // Match if ANY search term is found (more flexible search)
          return searchTerms.some(term => searchableText.includes(term));
        }).slice(0, 6);

        setResults(filtered);
        setIsOpen(filtered.length > 0 || query.trim().length >= 2);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(searchProducts, 150);
    return () => clearTimeout(debounce);
  }, [query]);

  const handleResultClick = (product) => {
    navigate(`/product/${product.slug}`);
    setQuery('');
    setIsOpen(false);
    setMobileSearchOpen(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/shop?search=${encodeURIComponent(query.trim())}`);
      setQuery('');
      setIsOpen(false);
      setMobileSearchOpen(false);
    }
  };

  return (
    <div ref={searchRef} className="relative">
      {/* Desktop Search */}
      <form onSubmit={handleSubmit} className="hidden md:block relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <Input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => query.trim().length >= 2 && setIsOpen(true)}
            placeholder="Caută sneakers..."
            className="w-56 lg:w-64 pl-10 pr-4 h-9 bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-none text-sm focus:bg-white/10 focus:border-white/20 transition-all"
            data-testid="desktop-search-input"
          />
        </div>
      </form>

      {/* Mobile Search Button */}
      <button
        onClick={() => {
          setMobileSearchOpen(!mobileSearchOpen);
          setTimeout(() => inputRef.current?.focus(), 100);
        }}
        className="md:hidden text-white/70 hover:text-white transition-colors p-1"
        data-testid="mobile-search-btn"
      >
        <Search className="w-5 h-5" />
      </button>

      {/* Mobile Search Expanded */}
      <AnimatePresence>
        {mobileSearchOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden fixed left-4 right-4 top-16 z-50"
          >
            <form onSubmit={handleSubmit} className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
              <Input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => query.trim().length >= 2 && setIsOpen(true)}
                placeholder="Caută sneakers..."
                className="w-full pl-11 pr-11 h-11 bg-[#121212] border-white/10 text-white placeholder:text-white/30 rounded-none text-sm"
                autoFocus
                data-testid="mobile-search-input"
              />
              <button
                type="button"
                onClick={() => {
                  setMobileSearchOpen(false);
                  setQuery('');
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search Results Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className={`bg-[#121212] border border-white/10 shadow-2xl z-50 ${
              mobileSearchOpen 
                ? 'fixed left-4 right-4 top-28' 
                : 'absolute top-full mt-2 left-0 w-full min-w-[280px]'
            }`}
            data-testid="search-results-dropdown"
          >
            {loading ? (
              <div className="p-4 text-center">
                <div className="w-5 h-5 border-2 border-white/20 border-t-[#c00000] rounded-full animate-spin mx-auto" />
              </div>
            ) : results.length > 0 ? (
              <div className="max-h-[400px] overflow-y-auto">
                {results.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => handleResultClick(product)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left"
                    data-testid={`search-result-${product.slug}`}
                  >
                    <img
                      src={product.images?.[0]}
                      alt={product.name}
                      className="w-12 h-12 object-cover bg-white/5"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-body text-sm text-white truncate">{product.name}</p>
                      <p className="font-body text-xs text-white/40">{product.category}</p>
                    </div>
                    <span className="font-heading text-sm text-[#c00000]">{product.price} RON</span>
                  </button>
                ))}
                {/* View All Results */}
                <button
                  onClick={handleSubmit}
                  className="w-full p-3 text-center text-sm text-[#c00000] hover:bg-white/5 border-t border-white/5 font-heading"
                >
                  Vezi toate rezultatele
                </button>
              </div>
            ) : (
              <div className="p-6 text-center">
                <Search className="w-8 h-8 text-white/10 mx-auto mb-2" />
                <p className="font-body text-sm text-white/50">Nu s-au găsit rezultate</p>
                <p className="font-body text-xs text-white/30 mt-1">Încearcă alte cuvinte cheie</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Layout = () => {
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const { itemCount } = useCart();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { name: 'MAGAZIN', href: '/shop' },
    { name: 'YEEZY 350', href: '/shop/yeezy-350-v2' },
    { name: 'LOUBOUTIN', href: '/shop/louboutin' },
    { name: 'MCQUEEN', href: '/shop/alexander-mcqueen' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Navigation */}
      <header className="fixed top-0 w-full z-50 backdrop-blur-xl bg-black/60 border-b border-white/5">
        <div className="px-6 md:px-12 lg:px-24">
          <nav className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <span className="font-heading font-bold text-2xl tracking-wider text-white">DEPRO</span>
              <span className="font-heading font-bold text-2xl tracking-wider text-[#c00000]">KICKS</span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-12">
              {navLinks.map((link) => (
                <Link 
                  key={link.name}
                  to={link.href}
                  className="font-heading font-bold text-sm tracking-widest text-white/70 hover:text-[#c00000] transition-colors"
                  data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-4">
              {/* Search Bar */}
              <SearchBar />
              {isAdmin && (
                <Link 
                  to="/admin"
                  className="hidden md:flex items-center gap-2 text-[#c00000] hover:text-white transition-colors"
                  data-testid="admin-link"
                >
                  <Shield className="w-5 h-5" />
                </Link>
              )}
              
              {isAuthenticated ? (
                <div className="hidden md:flex items-center gap-4">
                  <Link 
                    to="/profile"
                    className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
                    data-testid="profile-link"
                  >
                    <User className="w-5 h-5" />
                    <span className="font-body text-sm">{user?.name}</span>
                  </Link>
                  <button 
                    onClick={handleLogout}
                    className="text-white/50 hover:text-white transition-colors"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <Link 
                  to="/login"
                  className="hidden md:flex items-center gap-2 text-white/70 hover:text-white transition-colors"
                  data-testid="login-link"
                >
                  <User className="w-5 h-5" />
                  <span className="font-body text-sm">CONECTARE</span>
                </Link>
              )}

              <Link 
                to="/cart"
                className="relative flex items-center text-white hover:text-[#c00000] transition-colors"
                data-testid="cart-link"
              >
                <ShoppingBag className="w-6 h-6" />
                {itemCount > 0 && (
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#c00000] rounded-full flex items-center justify-center text-xs font-bold">
                    {itemCount}
                  </span>
                )}
              </Link>

              {/* Mobile Menu */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild className="md:hidden">
                  <button className="text-white p-2" data-testid="mobile-menu-btn">
                    <Menu className="w-6 h-6" />
                  </button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full max-w-sm bg-[#0a0a0a] border-white/10">
                  <div className="flex flex-col gap-8 mt-12">
                    {navLinks.map((link) => (
                      <Link
                        key={link.name}
                        to={link.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className="font-heading font-bold text-2xl tracking-wider text-white hover:text-[#c00000] transition-colors"
                      >
                        {link.name}
                      </Link>
                    ))}
                    <div className="border-t border-white/10 pt-8 flex flex-col gap-4">
                      {isAuthenticated ? (
                        <>
                          <Link 
                            to="/profile" 
                            onClick={() => setMobileMenuOpen(false)}
                            className="font-heading text-lg text-white/70"
                          >
                            CONTUL MEU
                          </Link>
                          {isAdmin && (
                            <Link 
                              to="/admin" 
                              onClick={() => setMobileMenuOpen(false)}
                              className="font-heading text-lg text-[#c00000]"
                            >
                              ADMIN
                            </Link>
                          )}
                          <button 
                            onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                            className="font-heading text-lg text-white/50 text-left"
                          >
                            DECONECTARE
                          </button>
                        </>
                      ) : (
                        <Link 
                          to="/login" 
                          onClick={() => setMobileMenuOpen(false)}
                          className="font-heading text-lg text-white"
                        >
                          CONECTARE
                        </Link>
                      )}
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-[#0a0a0a] border-t border-white/5 py-16">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div>
              <div className="flex items-center gap-2 mb-6">
                <span className="font-heading font-bold text-2xl tracking-wider text-white">DEPRO</span>
                <span className="font-heading font-bold text-2xl tracking-wider text-[#c00000]">KICKS</span>
              </div>
              <p className="font-body text-sm text-white/50 leading-relaxed">
                Sneakers premium pentru colecționari pretențioși. Peste 10.000 de clienți mulțumiți.
              </p>
            </div>
            <div>
              <h4 className="font-heading font-bold text-sm tracking-widest mb-6 text-white">MAGAZIN</h4>
              <div className="flex flex-col gap-3">
                <Link to="/shop/yeezy-350-v2" className="font-body text-sm text-white/50 hover:text-white transition-colors">Yeezy 350 V2</Link>
                <Link to="/shop/yeezy-380" className="font-body text-sm text-white/50 hover:text-white transition-colors">Yeezy 380</Link>
                <Link to="/shop/yeezy-500" className="font-body text-sm text-white/50 hover:text-white transition-colors">Yeezy 500</Link>
                <Link to="/shop/yeezy-700" className="font-body text-sm text-white/50 hover:text-white transition-colors">Yeezy 700</Link>
                <Link to="/shop/louboutin" className="font-body text-sm text-white/50 hover:text-white transition-colors">Louboutin</Link>
                <Link to="/shop/alexander-mcqueen" className="font-body text-sm text-white/50 hover:text-white transition-colors">Alexander McQueen</Link>
              </div>
            </div>
            <div>
              <h4 className="font-heading font-bold text-sm tracking-widest mb-6 text-white">SUPORT</h4>
              <div className="flex flex-col gap-3">
                <Link to="/return-policy" className="font-body text-sm text-white/50 hover:text-white transition-colors">Politica de Retur</Link>
                <Link to="/terms" className="font-body text-sm text-white/50 hover:text-white transition-colors">Termeni și Condiții</Link>
                <Link to="/contact" className="font-body text-sm text-white/50 hover:text-white transition-colors">Contact</Link>
                <a href="https://wa.me/40759649614" target="_blank" rel="noopener noreferrer" className="font-body text-sm text-[#25D366] hover:text-[#20BA5A] transition-colors">Suport WhatsApp</a>
              </div>
            </div>
            <div>
              <h4 className="font-heading font-bold text-sm tracking-widest mb-6 text-white">ÎNCREDERE</h4>
              <div className="flex flex-col gap-3">
                <span className="font-body text-sm text-white/50">Livrare Gratuită în România</span>
                <span className="font-body text-sm text-white/50">Retur în 14 Zile</span>
                <span className="font-body text-sm text-white/50">Plata la Livrare</span>
                <span className="font-body text-sm text-white/50">Calitate Premium</span>
              </div>
            </div>
          </div>
          <div className="border-t border-white/5 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="font-body text-xs text-white/30">© 2024 DEPRO KICKS. Toate drepturile rezervate.</p>
            <div className="flex gap-6">
              <Link to="/return-policy" className="font-body text-xs text-white/30 hover:text-white transition-colors">Politica de Retur</Link>
              <Link to="/terms" className="font-body text-xs text-white/30 hover:text-white transition-colors">Termeni și Condiții</Link>
              <Link to="/contact" className="font-body text-xs text-white/30 hover:text-white transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
